import { useState, useCallback } from "react";
import TickerContext from "../../helpers/contexts/tickerContext";
import type {
  TickerContentDataType,
  TickerDataType,
} from "../../types/widgets";

/**
 * Transform raw ticker data to the format used by TickerBanner
 */
const transformTickerData = (tickerContextData: TickerDataType[]): TickerContentDataType[] => {
  if (!tickerContextData || tickerContextData.length === 0) {
    return [];
  }

  return tickerContextData.map((d) => {
    return {
      name: d?.title ?? "",
      value: d?.value ?? "",
      value_formatter: d?.pre_text ?? "",
      indication: "",
      // Growth/decline arrow disabled — no previous period comparison for now
      // const currentValue = parseFloat(d?.value ?? "0");
      // const previousValue = parseFloat(d?.previous_value ?? "0");
      // indication:
      //   currentValue !== previousValue
      //     ? currentValue - previousValue > 0
      //       ? "up"
      //       : "down"
      //     : currentValue === 0
      //     ? ""
      //     : "up",
    };
  });
};

const TickerProvider = ({ children }) => {
  const [tickerData, setTickerData] = useState<TickerContentDataType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [useIndependentQuery, setUseIndependentQuery] = useState(false);

  // Add ticker data from dashboard response (legacy behavior)
  const addToTickerContext = useCallback((tickerContextData: TickerDataType[]) => {
    // Only use dashboard data if not using independent query
    if (!useIndependentQuery) {
      setTickerData(transformTickerData(tickerContextData));
    }
  }, [useIndependentQuery]);

  // Add ticker data from independent ticker query (new behavior)
  const addFromTickerQuery = useCallback((tickerContextData: TickerDataType[]) => {
    setUseIndependentQuery(true);
    setTickerData(transformTickerData(tickerContextData));
    setIsLoading(false);
  }, []);

  // Set loading state for independent ticker query
  const setTickerLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  // Reset ticker state (called when switching dashboards)
  const resetTickerState = useCallback(() => {
    setTickerData([]);
    setIsLoading(false);
    setUseIndependentQuery(false);
  }, []);

  return (
    <TickerContext.Provider
      value={{
        tickerData,
        setTickerData,
        addToTickerContext,
        addFromTickerQuery,
        setTickerLoading,
        resetTickerState,
        isLoading,
        useIndependentQuery,
      }}>
      {children}
    </TickerContext.Provider>
  );
};

export default TickerProvider;
